import React, { useMemo, useState, Fragment } from 'react';
import { XIcon, ArrowRightIcon, ArrowLeftIcon, SendIcon, PrinterIcon, LayoutGridIcon, ImageIcon, MonitorIcon, MapPinIcon, CheckCircleIcon, FileTextIcon } from 'lucide-react';
import { Store, Brand, BookingSlot, MediaType } from '../../data/types';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
interface BookingOpportunityModalProps {
  isOpen: boolean;
  stores: Store[];
  brands: Brand[];
  onClose: () => void;
  onSend: (request: any, sendNow?: boolean) => void;
}
const mediaTypeIcons: Record<MediaType, React.ReactNode> = {
  'Digital Outposts': <LayoutGridIcon className="w-4 h-4" data-id="element-728" />,
  'Digital Drive Aisle': <ImageIcon className="w-4 h-4" data-id="element-729" />,
  'Digital Windows': <MonitorIcon className="w-4 h-4" data-id="element-730" />
};
export function BookingOpportunityModal({
  isOpen,
  stores,
  brands,
  onClose,
  onSend
}: BookingOpportunityModalProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedBrandId, setSelectedBrandId] = useState<string>('');
  const [selectedSlotIds, setSelectedSlotIds] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState('');
  const [showAllBrands, setShowAllBrands] = useState(false);
  // Filter states for Step 2
  const [tierFilter, setTierFilter] = useState<string>('all');
  const [mediaFilter, setMediaFilter] = useState<string>('all');
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      maximumFractionDigits: 0
    }).format(amount);
  };
  const formatDates = (start: string, end: string) => {
    const opts: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'short'
    };
    return `${new Date(start).toLocaleDateString('en-ZA', opts)} – ${new Date(end).toLocaleDateString('en-ZA', opts)}`;
  };
  // Reset state on close
  const handleClose = () => {
    setStep(1);
    setSelectedBrandId('');
    setSelectedSlotIds(new Set());
    setMessage('');
    setShowAllBrands(false);
    setTierFilter('all');
    setMediaFilter('all');
    onClose();
  };
  const handleSend = () => {
    const brand = brands.find(b => b.id === selectedBrandId);
    if (!brand || selectedSlotIds.size === 0) return;
    // Extract unique stores and media types from selected slots
    const selectedStoresMap = new Map<string, {
      storeId: string;
      storeName: string;
    }>();
    const selectedMediaTypesSet = new Set<MediaType>();
    let earliestDate = '9999-12-31';
    let latestDate = '0000-01-01';
    let firstWeek = 999;
    let firstMediaType: MediaType = 'Digital Outposts';
    stores.forEach(store => {
      store.slots.forEach(slot => {
        if (selectedSlotIds.has(slot.id)) {
          selectedStoresMap.set(store.id, {
            storeId: store.id,
            storeName: store.name
          });
          selectedMediaTypesSet.add(slot.mediaType);
          if (slot.weekStartDate < earliestDate) earliestDate = slot.weekStartDate;
          if (slot.weekEndDate > latestDate) latestDate = slot.weekEndDate;
          if (slot.weekNumber < firstWeek) firstWeek = slot.weekNumber;
          firstMediaType = slot.mediaType;
        }
      });
    });
    const requestData = {
      brandId: brand.id,
      brandName: brand.name,
      stores: Array.from(selectedStoresMap.values()),
      mediaType: firstMediaType,
      mediaTypes: Array.from(selectedMediaTypesSet),
      weekNumber: firstWeek === 999 ? 1 : firstWeek,
      weekStartDate: earliestDate === '9999-12-31' ? new Date().toISOString() : earliestDate,
      weekEndDate: latestDate === '0000-01-01' ? new Date().toISOString() : latestDate,
      campaignName: `Media Quote – ${brand.name}`,
      message,
      contactName: brand.contacts?.[0]?.name,
      contactEmail: brand.contactEmail,
      requestedBy: 'woolworths'
    };
    onSend(requestData, true);
    handleClose();
  };
  // Derived data for Step 2
  const availableInventory = useMemo(() => {
    const result: {
      store: Store;
      availableSlots: BookingSlot[];
    }[] = [];
    stores.forEach(store => {
      if (tierFilter !== 'all' && store.tier !== tierFilter) return;
      const availableSlots = store.slots.filter(slot => {
        if (slot.status !== 'available') return false;
        if (mediaFilter !== 'all' && slot.mediaType !== mediaFilter) return false;
        return true;
      });
      if (availableSlots.length > 0) {
        result.push({
          store,
          availableSlots
        });
      }
    });
    return result;
  }, [stores, tierFilter, mediaFilter]);
  const allFilteredSlotIds = useMemo(() => {
    const ids = new Set<string>();
    availableInventory.forEach(({
      availableSlots
    }) => {
      availableSlots.forEach(slot => ids.add(slot.id));
    });
    return ids;
  }, [availableInventory]);
  const toggleSelectAll = () => {
    if (selectedSlotIds.size === allFilteredSlotIds.size) {
      setSelectedSlotIds(new Set());
    } else {
      setSelectedSlotIds(new Set(allFilteredSlotIds));
    }
  };
  const toggleSlot = (slotId: string) => {
    setSelectedSlotIds(prev => {
      const next = new Set(prev);
      if (next.has(slotId)) next.delete(slotId);else next.add(slotId);
      return next;
    });
  };
  // Derived data for Step 3
  const quoteData = useMemo(() => {
    const selectedBrand = brands.find(b => b.id === selectedBrandId);
    let grandTotal = 0;
    const groupedByStore: {
      store: Store;
      slots: BookingSlot[];
      subtotal: number;
    }[] = [];
    stores.forEach(store => {
      const selectedSlotsInStore = store.slots.filter(slot => selectedSlotIds.has(slot.id));
      if (selectedSlotsInStore.length > 0) {
        const subtotal = selectedSlotsInStore.reduce((sum, slot) => sum + slot.price, 0);
        grandTotal += subtotal;
        groupedByStore.push({
          store,
          slots: selectedSlotsInStore,
          subtotal
        });
      }
    });
    return {
      selectedBrand,
      groupedByStore,
      grandTotal
    };
  }, [stores, brands, selectedBrandId, selectedSlotIds]);
  if (!isOpen) return null;
  const displayedBrands = showAllBrands ? brands : brands.slice(0, 8);
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in print:bg-white print:p-0" data-id="element-731">
      <div className={`bg-white rounded-lg shadow-xl w-full flex flex-col max-h-[90vh] print:max-h-none print:shadow-none print:w-full print:h-full ${step === 3 ? 'max-w-4xl' : 'max-w-3xl'}`} data-id="element-732">
        {/* Modal Header - Hidden when printing */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border print:hidden" data-id="element-733">
          <div className="flex items-center gap-3" data-id="element-734">
            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center" data-id="element-735">
              <FileTextIcon className="w-5 h-5 text-blue-600" data-id="element-736" />
            </div>
            <div data-id="element-737">
              <h2 className="text-lg font-bold text-text-primary" data-id="element-738">
                Send Media Quote
              </h2>
              <p className="text-xs text-text-secondary" data-id="element-739">
                Build a curated quote of available media spaces
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="text-text-tertiary hover:text-text-primary transition-colors" data-id="element-740">
            <XIcon className="w-5 h-5" data-id="element-741" />
          </button>
        </div>

        {/* Step Indicator - Hidden when printing */}
        <div className="px-6 py-4 bg-surface-warm border-b border-border flex items-center justify-center gap-8 print:hidden" data-id="element-742">
          {[{
          num: 1,
          label: 'Select Brand'
        }, {
          num: 2,
          label: 'Select Slots'
        }, {
          num: 3,
          label: 'Preview Quote'
        }].map((s, i) => <div key={s.num} className="flex items-center gap-3" data-id="element-743">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step === s.num ? 'bg-primary text-white' : step > s.num ? 'bg-available text-white' : 'bg-border text-text-secondary'}`} data-id="element-744">
                {step > s.num ? <CheckCircleIcon className="w-4 h-4" data-id="element-745" /> : s.num}
              </div>
              <span className={`text-sm ${step === s.num ? 'font-semibold text-text-primary' : 'text-text-secondary'}`} data-id="element-746">
                {s.label}
              </span>
              {i < 2 && <div className="w-8 h-px bg-border ml-5" data-id="element-747" />}
            </div>)}
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6 print:p-0 print:overflow-visible" data-id="element-748">
          {/* STEP 1: Select Brand */}
          {step === 1 && <div className="space-y-6 animate-fade-in" data-id="element-749">
              <div className="flex items-center justify-between mb-4" data-id="element-750">
                <h3 className="text-base font-semibold text-text-primary" data-id="element-751">
                  Select a brand to send this opportunity to
                </h3>
                {brands.length > 8 && <button onClick={() => setShowAllBrands(!showAllBrands)} className="text-sm text-primary hover:underline" data-id="element-752">
                    {showAllBrands ? 'Show less' : 'Show all'}
                  </button>}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-753">
                {displayedBrands.map(brand => <label key={brand.id} className={`
                      flex items-center p-4 border rounded-lg cursor-pointer transition-all
                      ${selectedBrandId === brand.id ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:border-text-tertiary hover:bg-surface'}
                    `} data-id="element-754">
                    <input type="radio" name="brand" value={brand.id} checked={selectedBrandId === brand.id} onChange={() => setSelectedBrandId(brand.id)} className="sr-only" data-id="element-755" />
                    <div className="flex-1" data-id="element-756">
                      <div className="font-semibold text-text-primary" data-id="element-757">
                        {brand.name}
                      </div>
                      <div className="text-xs text-text-secondary mt-1" data-id="element-758">
                        {brand.category}
                      </div>
                    </div>
                    {selectedBrandId === brand.id && <CheckCircleIcon className="w-5 h-5 text-primary" data-id="element-759" />}
                  </label>)}
              </div>
            </div>}

          {/* STEP 2: Select Slots */}
          {step === 2 && <div className="space-y-6 animate-fade-in flex flex-col h-full" data-id="element-760">
              <div className="flex items-center justify-between bg-surface p-3 rounded-lg border border-border" data-id="element-761">
                <div className="flex items-center gap-4" data-id="element-762">
                  <select value={tierFilter} onChange={e => setTierFilter(e.target.value)} className="text-sm border-none bg-transparent focus:ring-0 text-text-primary font-medium cursor-pointer" data-id="element-763">
                    <option value="all" data-id="element-764">All Store Tiers</option>
                    <option value="flagship" data-id="element-765">Flagship Only</option>
                    <option value="high-traffic" data-id="element-766">High Traffic Only</option>
                    <option value="standard" data-id="element-767">Standard Only</option>
                  </select>
                  <div className="w-px h-4 bg-border" data-id="element-768" />
                  <select value={mediaFilter} onChange={e => setMediaFilter(e.target.value)} className="text-sm border-none bg-transparent focus:ring-0 text-text-primary font-medium cursor-pointer" data-id="element-769">
                    <option value="all" data-id="element-770">All Media Types</option>
                    <option value="Digital Outposts" data-id="element-771">Digital Outposts</option>
                    <option value="Digital Drive Aisle" data-id="element-772">
                      Digital Drive Aisle
                    </option>
                    <option value="Digital Windows" data-id="element-773">Digital Windows</option>
                  </select>
                </div>
                <Button variant="ghost" size="sm" onClick={toggleSelectAll} className="text-primary" data-id="element-774">
                  {selectedSlotIds.size === allFilteredSlotIds.size && allFilteredSlotIds.size > 0 ? 'Deselect All' : 'Select All Visible'}
                </Button>
              </div>

              <div className="space-y-6" data-id="element-775">
                {availableInventory.length === 0 ? <div className="text-center py-12 text-text-secondary" data-id="element-776">
                    No available slots match your filters.
                  </div> : availableInventory.map(({
              store,
              availableSlots
            }) => <div key={store.id} className="border border-border rounded-lg overflow-hidden" data-id="element-777">
                      <div className="bg-surface-warm px-4 py-3 border-b border-border flex items-center justify-between" data-id="element-778">
                        <div className="flex items-center gap-2" data-id="element-779">
                          <h4 className="font-semibold text-text-primary" data-id="element-780">
                            {store.name}
                          </h4>
                          <span className="text-xs text-text-secondary flex items-center gap-1" data-id="element-781">
                            <MapPinIcon className="w-3 h-3" data-id="element-782" /> {store.location}
                          </span>
                        </div>
                        <Badge variant={store.tier} data-id="element-783">
                          {store.tier.replace('-', ' ')}
                        </Badge>
                      </div>
                      <div className="divide-y divide-border" data-id="element-784">
                        {availableSlots.map(slot => <label key={slot.id} className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors ${selectedSlotIds.has(slot.id) ? 'bg-primary/5' : 'hover:bg-surface'}`} data-id="element-785">
                            <div className="flex items-center gap-4" data-id="element-786">
                              <input type="checkbox" checked={selectedSlotIds.has(slot.id)} onChange={() => toggleSlot(slot.id)} className="w-4 h-4 rounded-sm border-border text-primary focus:ring-primary cursor-pointer" data-id="element-787" />
                              <div data-id="element-788">
                                <div className="flex items-center gap-2" data-id="element-789">
                                  <span className="text-sm font-medium text-text-primary" data-id="element-790">
                                    Week {slot.weekNumber}
                                  </span>
                                  <span className="text-xs text-text-tertiary" data-id="element-791">
                                    {formatDates(slot.weekStartDate, slot.weekEndDate)}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5 text-xs text-text-secondary mt-1" data-id="element-792">
                                  {mediaTypeIcons[slot.mediaType]}
                                  {slot.mediaType}
                                </div>
                              </div>
                            </div>
                            <div className="text-sm font-semibold text-text-primary" data-id="element-793">
                              {formatCurrency(slot.price)}
                            </div>
                          </label>)}
                      </div>
                    </div>)}
              </div>
            </div>}

          {/* STEP 3: Preview Quote */}
          {step === 3 && quoteData.selectedBrand && <div className="animate-fade-in space-y-6 print:space-y-0" data-id="element-794">
              {/* The Quote Document */}
              <div className="bg-white border border-border rounded-lg shadow-sm p-8 print:border-none print:shadow-none print:p-0" data-id="element-795">
                {/* Quote Header */}
                <div className="flex justify-between items-start mb-10 border-b border-border pb-6" data-id="element-796">
                  <div data-id="element-797">
                    <h1 className="text-3xl font-bold text-gray-900 tracking-tight mb-1" data-id="element-798">
                      BOOKING OPPORTUNITY
                    </h1>
                    <p className="text-sm text-gray-500" data-id="element-799">
                      Generated on{' '}
                      {new Date().toLocaleDateString('en-ZA', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                    </p>
                  </div>
                  <div className="text-right" data-id="element-800">
                    <div className="text-xl font-black tracking-widest text-gray-900" data-id="element-801">
                      GRAPEVINE MEDIA
                    </div>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-802">
                      Woolworths Retail Media Partner
                    </p>
                  </div>
                </div>

                {/* Quote To */}
                <div className="flex justify-between items-end mb-8" data-id="element-803">
                  <div data-id="element-804">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-805">
                      Prepared For
                    </p>
                    <h2 className="text-xl font-bold text-gray-900" data-id="element-806">
                      {quoteData.selectedBrand.name}
                    </h2>
                    {quoteData.selectedBrand.contacts && quoteData.selectedBrand.contacts.length > 0 && <p className="text-sm text-gray-600 mt-1" data-id="element-807">
                          Attn: {quoteData.selectedBrand.contacts[0].name} (
                          {quoteData.selectedBrand.contacts[0].email})
                        </p>}
                  </div>
                  <div className="text-right bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-808">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-809">
                      Opportunity Summary
                    </p>
                    <p className="text-sm text-gray-900 font-medium" data-id="element-810">
                      {selectedSlotIds.size} premium slots across{' '}
                      {quoteData.groupedByStore.length} stores
                    </p>
                    <p className="text-2xl font-bold text-primary mt-1" data-id="element-811">
                      {formatCurrency(quoteData.grandTotal)}
                    </p>
                  </div>
                </div>

                {/* Quote Table */}
                <div className="mb-8" data-id="element-812">
                  <table className="w-full text-left border-collapse" data-id="element-813">
                    <thead data-id="element-814">
                      <tr className="border-b-2 border-gray-900" data-id="element-815">
                        <th className="py-3 text-xs font-bold text-gray-900 uppercase tracking-wider w-1/3" data-id="element-816">
                          Store / Location
                        </th>
                        <th className="py-3 text-xs font-bold text-gray-900 uppercase tracking-wider" data-id="element-817">
                          Media & Timing
                        </th>
                        <th className="py-3 text-xs font-bold text-gray-900 uppercase tracking-wider text-right" data-id="element-818">
                          Investment
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-819">
                      {quoteData.groupedByStore.map(({
                    store,
                    slots,
                    subtotal
                  }) => <Fragment key={store.id} data-id="element-820">
                            {/* Store Header Row */}
                            <tr className="border-b border-gray-200 bg-gray-50/50" data-id="element-821">
                              <td className="py-3 font-bold text-gray-900" colSpan={3} data-id="element-822">
                                {store.name}{' '}
                                <span className="font-normal text-gray-500 text-sm ml-2" data-id="element-823">
                                  {store.location} ·{' '}
                                  {store.tier.replace('-', ' ')}
                                </span>
                              </td>
                            </tr>
                            {/* Slot Rows */}
                            {slots.map(slot => <tr key={slot.id} className="border-b border-gray-100" data-id="element-824">
                                <td className="py-3 pl-4" data-id="element-825"></td>
                                <td className="py-3" data-id="element-826">
                                  <div className="flex items-center gap-2" data-id="element-827">
                                    <span className="text-sm font-medium text-gray-900" data-id="element-828">
                                      {slot.mediaType}
                                    </span>
                                    <span className="text-xs text-gray-500 px-2 py-0.5 bg-gray-100 rounded" data-id="element-829">
                                      Week {slot.weekNumber}
                                    </span>
                                  </div>
                                  <div className="text-xs text-gray-500 mt-0.5" data-id="element-830">
                                    {formatDates(slot.weekStartDate, slot.weekEndDate)}
                                  </div>
                                </td>
                                <td className="py-3 text-right text-sm font-medium text-gray-900" data-id="element-831">
                                  {formatCurrency(slot.price)}
                                </td>
                              </tr>)}
                            {/* Store Subtotal Row */}
                            <tr className="border-b border-gray-200" data-id="element-832">
                              <td colSpan={2} className="py-2 text-right text-xs font-bold text-gray-500 uppercase" data-id="element-833">
                                Subtotal {store.name}
                              </td>
                              <td className="py-2 text-right text-sm font-bold text-gray-900" data-id="element-834">
                                {formatCurrency(subtotal)}
                              </td>
                            </tr>
                          </Fragment>)}
                      {/* Grand Total Row */}
                      <tr className="bg-gray-900 text-white" data-id="element-835">
                        <td colSpan={2} className="py-4 px-4 text-right text-sm font-bold uppercase tracking-wider" data-id="element-836">
                          Total Investment Required
                        </td>
                        <td className="py-4 px-4 text-right text-xl font-bold" data-id="element-837">
                          {formatCurrency(quoteData.grandTotal)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Quote Footer */}
                <div className="text-center text-xs text-gray-500 border-t border-gray-200 pt-6 mt-12" data-id="element-838">
                  <p data-id="element-839">
                    This media quote is valid for 7 days and subject to
                    availability at the time of confirmation.
                  </p>
                  <p className="mt-1" data-id="element-840">
                    Please contact your Grapevine representative to secure these
                    spaces.
                  </p>
                </div>
              </div>

              {/* Optional Message - Hidden when printing */}
              <div className="print:hidden" data-id="element-841">
                <label className="block text-sm font-medium text-text-primary mb-2" data-id="element-842">
                  Add a personal message to {quoteData.selectedBrand.name}{' '}
                  (Optional)
                </label>
                <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="e.g. Hi team, I've put together a package of premium flagship stores for your upcoming launch..." className="w-full h-24 px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary resize-none" data-id="element-843" />
              </div>
            </div>}
        </div>

        {/* Footer Actions - Hidden when printing */}
        <div className="px-6 py-4 border-t border-border bg-surface flex items-center justify-between print:hidden" data-id="element-844">
          {step === 1 ? <Button variant="ghost" size="sm" onClick={handleClose} data-id="element-845">
              Cancel
            </Button> : <Button variant="ghost" size="sm" onClick={() => setStep(step - 1 as 1 | 2)} className="flex items-center gap-1.5" data-id="element-846">
              <ArrowLeftIcon className="w-4 h-4" data-id="element-847" /> Back
            </Button>}

          {step === 1 && <Button variant="primary" onClick={() => setStep(2)} disabled={!selectedBrandId} className="flex items-center gap-1.5" data-id="element-848">
              Next <ArrowRightIcon className="w-4 h-4" data-id="element-849" />
            </Button>}

          {step === 2 && <div className="flex items-center gap-4" data-id="element-850">
              <div className="text-sm font-medium text-text-secondary" data-id="element-851">
                {selectedSlotIds.size} slots selected
              </div>
              <Button variant="primary" onClick={() => setStep(3)} disabled={selectedSlotIds.size === 0} className="flex items-center gap-1.5" data-id="element-852">
                Preview Quote <ArrowRightIcon className="w-4 h-4" data-id="element-853" />
              </Button>
            </div>}

          {step === 3 && <div className="flex items-center gap-3" data-id="element-854">
              <Button variant="secondary" onClick={() => window.print()} className="flex items-center gap-1.5" data-id="element-855">
                <PrinterIcon className="w-4 h-4" data-id="element-856" /> Print / Export PDF
              </Button>
              <Button variant="primary" onClick={handleSend} className="flex items-center gap-1.5 bg-available hover:bg-available/90" data-id="element-857">
                <SendIcon className="w-4 h-4" data-id="element-858" /> Send to Brand
              </Button>
            </div>}
        </div>
      </div>
    </div>;
}